import ClientCache from "@/utils/login/ClientCache";
import ShortCutsUtil from "@/utils/common/ShortCutsUtil";
import Translator from "@/utils/common/Translator";
import {Cookies} from 'typescript-cookie';
import {useLoginInfoStore} from "@/stores/loginInfo";
import type {AxiosRequestConfig} from "axios";
import NamaCommon, {NamaAxiosRequestConfig} from "@/utils/common/NamaCommon";
import NamaURLS from "@/utils/constants/NamaURLS";
import {useLoadingStore} from "@/stores/LoadingStore";
import AutoLogoutTime from "@/utils/login/AutoLogoutTime";
import {useErrorsStore, useMessagesStore} from "@/stores/ToastDataStore.ts";
import {Ref, ref} from "vue";
import {QuestionFieldsWithValues} from "@/utils/presenters/interfaces.ts";
import CommonFieldIds from "@/utils/constants/CommonFieldIds.ts";
import DomainBaseEntities from "@/utils/constants/DomainBaseEntities.ts";
import CommonConstants from "@/utils/constants/CommonConstants.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import FieldTypesUtils from "@/utils/common/FieldTypesUtils.ts";
import {Dialog} from "quasar";
import LoginByQRDialog from "@/components/dialogs/LoginByQRDialog.vue";
import AttachmentLinkUtil from "@/utils/UIConstants/AttachmentLinkUtil.ts";
import NaMaErrors from "@/utils/constants/NaMaErrors.ts";
import PreferencesCache from "@/utils/cache/PreferencesCache.ts";
import DownloadPurpose from "@/utils/constants/DownloadPurpose.ts";
import {namaBroadcast} from "@/utils/broadcast/NamaBroadcastChannel.ts";
import {namaWebSocket} from "@/utils/broadcast/NamaWebSocketManager.ts";
import NotificationUtils from "@/utils/broadcast/NotificationUtils.ts";
import CurrentLoginInfo = namaTypes.CurrentLoginInfo;
import QuestionField = namaTypes.QuestionField;
import DTOFilledQuestionField = namaTypes.DTOFilledQuestionField;
import EntityDimensionsDTO = namaTypes.EntityDimensionsDTO;
import LoginRequest = namaTypes.LoginRequest;
import ResetPasswordByTokenRequest = namaTypes.ResetPasswordByTokenRequest;
import ResetPasswordRequest = namaTypes.ResetPasswordRequest;
import DefaultApplicationInfo from "@/utils/common/DefaultApplicationInfo.ts";

export default class LoginUtils {
  private static _loginInfo: Ref<namaTypes.CurrentLoginInfo | null> = ref(null);
  static userImageSrc: Ref<string | null> = ref(null);
  static RESET_PASSWORD_CALLBACK: () => Promise<void>;

  static get loginInfo(): namaTypes.CurrentLoginInfo | null {
    return this._loginInfo.value;
  }

  static getCurrentUserImageSrc(): string {
    const fileId = this.loginInfo?.userInfo?.mainFile?.attachmentInfo?.fileId ?? '';
    return this.getUserImageSrc(fileId, true, 'fileID');
  }

  static getUserImageSrc(id: string, noCache: boolean, idParamName?: string): string {
    if (!idParamName)
      idParamName = "recordId";
    let url = AttachmentLinkUtil.downloadURL(idParamName, id, "User", DownloadPurpose.THUMBNAIL, false, false) + "&userImage=true";
    if (noCache)
      url += "&ts=" + Date.now();
    return url;
  }

  static async tryLoginFromSessionOrLogout() {
    if (await this.tryLoginFromSession())
      return true;
    await this.logout();
    return false;
  }

  static async onLoginSuccess(result: namaTypes.LoginAndGUIPartsResponse) {
    let loginInfo: CurrentLoginInfo = result.loginInfo || {};
    // Validate preferences cache against server cacheId
    await PreferencesCache.validateCacheId(result.loginInfo?.cacheId);
    if (loginInfo.userInfo?.settings?.passwordMustBeChanged) {
      if (this.RESET_PASSWORD_CALLBACK) {
        this._loginInfo.value = loginInfo;
        this.userImageSrc.value = this.getCurrentUserImageSrc();
        await this.RESET_PASSWORD_CALLBACK();
      }
      return;
    }
    if (!loginInfo.legalEntitySelected) {
      // TODO:: showSelectLegalEntityScreen;
      console.log("Show Select Legal Entity Screen");
      return;
    }
    this._loginInfo.value = loginInfo;
    this.userImageSrc.value = this.getCurrentUserImageSrc();
    const loginInfoStore = useLoginInfoStore();
    await ClientCache.onLogin(result);
    await DefaultApplicationInfo.updateSupportDateAlerts();
    NotificationUtils.syncSoundsAfterLogin();
    loginInfoStore.login(loginInfo);
    namaBroadcast.init();
    namaBroadcast.setupStoreSync();
    namaWebSocket.init();
    AutoLogoutTime.registerIfNeeded(loginInfo);
    Cookies.set("dmsdeviceid", loginInfo.deviceId, {expires: 30});
    ShortCutsUtil.registerShortcutKeys(loginInfo.shortCuts);
    localStorage.setItem("lastlogintimestamp", ClientCache.currentTimeMillis() + "");
    if (loginInfo.preferedLanguage) {
      let preferredLocale = loginInfo.preferedLanguage === 'English' ? 'en' : 'ar';
      localStorage.setItem("lastPreferredLocale", preferredLocale);
      if (preferredLocale !== Translator.locale && !Translator.localeFromUrl)
        window.location.reload();
    } else {
      let lastPreferredLocale = localStorage.getItem("lastPreferredLocale");
      localStorage.removeItem("lastPreferredLocale");
      if (!Translator.localeFromUrl && lastPreferredLocale === 'en')
        window.location.reload();
    }
  }

  static async tryLoginFromSession() {
    const settings: NamaAxiosRequestConfig = {
      data: {deviceId: Cookies.get("dmsdeviceid")},
      noLoadingIndicator: true,
    };
    let data = await NamaCommon.namaFetch(NamaURLS.LOGIN, settings);
    if (!data.loginInfo?.loggedIn)
      return false;
    await LoginUtils.onLoginSuccess(data);
    return true;
  }

  static async login(userName: string, password: string, staySignedIn: boolean, otpId?: string, otpValue?: string): Promise<Record<any, any> | undefined> {
    localStorage.setItem("rememberme", staySignedIn + '');
    let loginRequest: LoginRequest = {
      userName,
      password,
      remember: staySignedIn
    };
    if (otpId && otpValue) {
      loginRequest.otpId = otpId;
      loginRequest.otpValue = otpValue;
    }
    const settings: NamaAxiosRequestConfig = {
      data: loginRequest,
      noLoadingIndicator: true,
    };
    let data = await NamaCommon.namaFetch(NamaURLS.LOGIN, settings)
    if (this.isLoginOtpRequired(data) && data.errors && data.errors.length && data.errors[0].extraInfo)
      return data.errors[0].extraInfo;
    if (data.loginInfo && data.loginInfo.loggedIn) {
      await LoginUtils.onLoginSuccess(data);
    }
    return;
  }

  private static isLoginOtpRequired(data: any): boolean {
    if (data.errorType)
      return data.errorType === NaMaErrors.LOGIN_OTP_REQUIRED;
    return false;
  }

  static async sendResetToken(emailOrPhone: string, isEmail: boolean, username: string): Promise<boolean> {
    const request: ResetPasswordRequest = {
      loginId: username,
      sendEmail: isEmail,
      sendSMS: !isEmail,
      email: emailOrPhone,
      phoneNumber: emailOrPhone
    };
    let data = await NamaCommon.namaFetch(NamaURLS.ForgotPassword, {data: request})
    return !data.errors || !data.errors.length;
  }

  static async resendOtpMsg(username: string, otpId: string): Promise<string> {
    let res = await NamaCommon.namaFetch(NamaURLS.ResendOtpMsg, {params: {username, otpId}});
    if (res && res.data) {
      useMessagesStore().pushMessageStr(Translator.translateId("otpResentSuccessfully"));
      return res.data ?? "";
    }
    return "";
  }

  static async resetPassword(username: string, token: string, userNewPassword: string, confirmNewPassword: string) {
    const request: ResetPasswordByTokenRequest = {
      loginId: username,
      resetToken: token,
      password: userNewPassword,
      confirmPassword: confirmNewPassword
    };
    let data = await NamaCommon.namaFetch(NamaURLS.ResetPasswordByToken, {data: request})
    return !data.errors || !data.errors.length;
  }

  static isCurrentLogInInfoValid(): boolean {
    return Boolean(this.loginInfo) && Boolean(this.loginInfo?.loggedIn);
  }

  static async refreshLogin() {
    let loadingStore = useLoadingStore();
    loadingStore.startLoading();
    const loginInfoStore = useLoginInfoStore()
    loginInfoStore.logout();
    await this.tryLoginFromSession();
    loadingStore.stopLoading();
  }

  static async logout() {
    let loadingStore = useLoadingStore();
    loadingStore.startLoading();
    const loginInfoStore = useLoginInfoStore()
    loginInfoStore.logout();
    const settings: AxiosRequestConfig = {
      params: {
        logoutInfo: JSON.stringify({
          deviceId: Cookies.get("dmsdeviceid"),
        })
      },
    };
    Cookies.remove("dmsdeviceid");
    // Cleanup WebSocket and BroadcastChannel
    await namaWebSocket.destroy();
    namaBroadcast.destroy();
    await NamaCommon.namaFetch(NamaURLS.LOGOUT, settings);
    LoginUtils.logoutCallback();
    loadingStore.stopLoading();
  }

  private static logoutCallback() {
    localStorage.setItem("lastlogouttimestamp", ClientCache.currentTimeMillis() + "");
    ShortCutsUtil.registerShortcutKeys(null);
    this._loginInfo.value = null;
  }

  static async reLogin(loginInfo: CurrentLoginInfo) {
    // TODO:: if showPasswordMustBeChangedDialog is true => LoginPresenterImpl.showSetNewPassword(loginInfo);

    // TODO:: updateCurrentLoginInfo(loginInfo);

    let request: namaTypes.ReloginInfo = {
      barCodeOn: loginInfo.barCodeOn,
      alwaysPrint1InBarcode: loginInfo.alwaysPrint1InBarcode,
      directPrint: loginInfo.directPrint,
      hijriDate: loginInfo.hijriDate,
      printDocumentsOnSave: loginInfo.userInfo?.settings?.printDocumentsOnSave
    };
    let response = await NamaCommon.namaFetch(NamaURLS.ReLogin, {data: request});
    if (response.failureOccurred)
      useErrorsStore().pushErrorStr(Translator.translateId('Could not change login context'));
  }

  static createDimensionsDialogQuestionFieldsWithDefaultValues(): QuestionFieldsWithValues {
    let questionFields: QuestionField[] = [];
    questionFields.push(NamaCommon.createQuestionFieldRef(CommonFieldIds.LEGAL_ENTITY, DomainBaseEntities.LegalEntity, undefined, true));
    questionFields.push(NamaCommon.createQuestionFieldRef(CommonFieldIds.SECTOR, DomainBaseEntities.Sector, undefined, true));
    questionFields.push(NamaCommon.createQuestionFieldRef(CommonFieldIds.BRANCH, DomainBaseEntities.Branch, undefined, true));
    questionFields.push(NamaCommon.createQuestionFieldRef(CommonFieldIds.DEPARTMENT, DomainBaseEntities.Department, undefined, true));
    questionFields.push(NamaCommon.createQuestionFieldRef(CommonFieldIds.ANALYSISSET, DomainBaseEntities.AnalysisSet, undefined, true));
    questionFields.push(NamaCommon.createQuestionField(CommonConstants.BARCODEON, FieldType.Boolean));
    questionFields.push(NamaCommon.createQuestionField(CommonConstants.ALWAYS_PRINT_ONE_BARCODE, FieldType.Boolean));
    questionFields.push(NamaCommon.createQuestionField(CommonConstants.DIRECT_PRINT, FieldType.Boolean));
    questionFields.push(NamaCommon.createQuestionField(CommonConstants.HIJRI_DATE, FieldType.Boolean));
    questionFields.push(NamaCommon.createQuestionField("printDocumentsOnSave", FieldType.Boolean));

    let dimensions: EntityDimensionsDTO | undefined = LoginUtils.loginInfo?.dimensions;
    let defaultValues: DTOFilledQuestionField[] = [
      {
        id: CommonFieldIds.LEGAL_ENTITY,
        value: FieldTypesUtils.toString(FieldType.Reference, dimensions?.legalEntity),
        objValue: dimensions?.legalEntity
      },
      {id: CommonFieldIds.SECTOR, value: FieldTypesUtils.toString(FieldType.Reference, dimensions?.sector), objValue: dimensions?.sector},
      {id: CommonFieldIds.BRANCH, value: FieldTypesUtils.toString(FieldType.Reference, dimensions?.branch), objValue: dimensions?.branch},
      {id: CommonFieldIds.DEPARTMENT, value: FieldTypesUtils.toString(FieldType.Reference, dimensions?.department), objValue: dimensions?.department},
      {
        id: CommonFieldIds.ANALYSISSET,
        value: FieldTypesUtils.toString(FieldType.Reference, dimensions?.analysisSet),
        objValue: dimensions?.analysisSet
      },
      {
        id: CommonConstants.BARCODEON,
        value: FieldTypesUtils.toString(FieldType.Boolean, LoginUtils.loginInfo?.barCodeOn),
        objValue: LoginUtils.loginInfo?.barCodeOn
      },
      {
        id: CommonConstants.ALWAYS_PRINT_ONE_BARCODE,
        value: FieldTypesUtils.toString(FieldType.Boolean, LoginUtils.loginInfo?.alwaysPrint1InBarcode),
        objValue: LoginUtils.loginInfo?.alwaysPrint1InBarcode
      },
      {
        id: CommonConstants.DIRECT_PRINT,
        value: FieldTypesUtils.toString(FieldType.Boolean, LoginUtils.loginInfo?.directPrint),
        objValue: LoginUtils.loginInfo?.directPrint
      },
      {
        id: CommonConstants.HIJRI_DATE,
        value: FieldTypesUtils.toString(FieldType.Boolean, LoginUtils.loginInfo?.hijriDate),
        objValue: LoginUtils.loginInfo?.hijriDate
      },
      {
        id: "printDocumentsOnSave",
        value: FieldTypesUtils.toString(FieldType.Boolean, LoginUtils.loginInfo?.userInfo?.settings?.printDocumentsOnSave),
        objValue: LoginUtils.loginInfo?.userInfo?.settings?.printDocumentsOnSave
      }
    ];
    return {questionFields, defaultValues} as QuestionFieldsWithValues;
  }

  static async onDimensionsSelected(object: Record<string, any>) {
    let loginInfo: CurrentLoginInfo | null = LoginUtils.loginInfo;
    if (!loginInfo)
      return;
    loginInfo.dimensions = {
      legalEntity: object[CommonFieldIds.LEGAL_ENTITY],
      sector: object[CommonFieldIds.SECTOR],
      branch: object[CommonFieldIds.BRANCH],
      department: object[CommonFieldIds.DEPARTMENT],
      analysisSet: object[CommonFieldIds.ANALYSISSET],
    };
    loginInfo.barCodeOn = object[CommonConstants.BARCODEON];
    loginInfo.hijriDate = object[CommonConstants.HIJRI_DATE];
    loginInfo.alwaysPrint1InBarcode = object[CommonConstants.ALWAYS_PRINT_ONE_BARCODE];
    loginInfo.directPrint = object[CommonConstants.DIRECT_PRINT];
    if (loginInfo.userInfo && loginInfo.userInfo.settings)
      loginInfo.userInfo.settings.printDocumentsOnSave = object.printDocumentsOnSave;

    await this.reLogin(loginInfo);
  }

  static currentUserId() {
    if (this.loginInfo && this.loginInfo.userInfo)
      return this.loginInfo.userInfo.id;
    return undefined;
  }

  static currentUserInfo() {
    if (!this.loginInfo || !this.loginInfo.userInfo)
      return undefined;
    return this.loginInfo.userInfo;
  }

  static currentUserEmployeeId(): string | undefined {
    if (this.loginInfo && this.loginInfo.userInfo && this.loginInfo.userInfo.employee)
      return this.loginInfo.userInfo.employee.id;
    return undefined;
  }

  static getBarCodeOn(): boolean {
    return Boolean(this.loginInfo) && Boolean(this.loginInfo!.barCodeOn);
  }

  static currentUserIsAdminOrTreatAsAdmin(): boolean {
    if (!this.currentUserInfo())
      return false;
    if (this.currentUserInfo()?.settings?.treatAsAdmin)
      return true;
    if (this.currentUserInfo()?.fullProfile && this.currentUserInfo()?.fullProfile?.fullAuthority)
      return true;
    return false;
  }

  static isCurrentUserAdminOrTreatAsAdmin(): boolean {
    const info = this.currentUserInfo();
    if (!info)
      return false;
    return info.code === 'admin' || !!info.settings?.treatAsAdmin;
  }

  static async showLoginByQR() {
    await this.genQRCodeForMPSLogin((password) => {
      if ((window as any).updatePassword)
        (window as any).updatePassword(password);
    })
  }

  static async genQRCodeForMPSLogin(callback: (password: string) => void) {
    const uuid = await ClientCache.asyncNextGUIDId()
    const reqId = "mpsreq:" + uuid
    Dialog.create({
      component: LoginByQRDialog,
      componentProps: {
        reqId
      }
    })
    this.scheduleNextMPSCheck(0, reqId, callback)
  }

  static scheduleNextMPSCheck(i: number, request: string, callback: (result: string) => void) {
    if (i >= 60) {
      useLoginInfoStore().qrCodeDialogVisible = false
      window.alert("Could not authenticate - exiting now");
      return;
    }
    setTimeout(
      async () => {
        let response = await NamaCommon.namaFetch(this.genMPSAuthCheckURL(request), {method: "GET", noLoadingIndicator: true});
        if (!response) {
          this.scheduleNextMPSCheck(i + 1, request, callback);
        } else {
          useLoginInfoStore().qrCodeDialogVisible = false
          callback(response);
        }
        return false;
      }, 1000);
  }

  static genMPSAuthCheckURL(request: string) {
    const crmURL = "https://crm.namasoft.com/erp/";
    return crmURL + "utils?" + CommonConstants.MPS_AUTH_RETRIEVE + "=" + request;
  }

  static genMPSAuthURL(reqId: string) {
    const crmURL = "https://crm.namasoft.com/erp/";
    const customerName = (window as any).customer;
    if (customerName) {
      reqId = reqId + "~" + customerName;
    }
    return crmURL + "utils?" + CommonConstants.MPS_AUTH + "=" + reqId;
  }
}
